import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, of, type Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  type JobChatMessagesApiResponse,
  type JobChatMessagesGetResult,
  type JobChatSendApiResponse,
  type JobChatSendResult,
} from './job-chat.types';

function messagesResultFromEnvelope(res: JobChatMessagesApiResponse): JobChatMessagesGetResult {
  if (res.status && res.data) {
    return { outcome: 'success', data: res.data };
  }
  const msg = res.message?.trim();
  if (msg) {
    return { outcome: 'apiMessage', message: msg, responseCode: res.responseCode };
  }
  return { outcome: 'failure' };
}

function sendResultFromEnvelope(res: JobChatSendApiResponse): JobChatSendResult {
  if (res.status && res.data) {
    return { outcome: 'success', data: res.data };
  }
  const msg = res.message?.trim();
  if (msg) {
    return { outcome: 'apiMessage', message: msg, responseCode: res.responseCode };
  }
  return { outcome: 'failure' };
}

/** When the server uses HTTP 4xx/5xx, Angular errors before `map`; the JSON body is still here. */
function messagesResultFromHttpError(error: unknown): JobChatMessagesGetResult | null {
  const body = parseErrorBody(error);
  if (!body) {
    return null;
  }
  return messagesResultFromEnvelope({
    status: Boolean(body.status),
    responseCode: typeof body.responseCode === 'number' ? body.responseCode : 0,
    message: body.message,
    data: null,
  });
}

function sendResultFromHttpError(error: unknown): JobChatSendResult | null {
  const body = parseErrorBody(error);
  if (!body) {
    return null;
  }
  return sendResultFromEnvelope({
    status: Boolean(body.status),
    responseCode: typeof body.responseCode === 'number' ? body.responseCode : 0,
    message: body.message,
    data: null,
  });
}

function parseErrorBody(error: unknown): { status?: unknown; responseCode?: unknown; message: string } | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  let body: unknown = error.error;
  if (typeof body === 'string') {
    try {
      body = JSON.parse(body) as unknown;
    } catch {
      return null;
    }
  }
  if (!body || typeof body !== 'object') {
    return null;
  }
  const o = body as { status?: unknown; responseCode?: unknown; message?: unknown };
  if (typeof o.message !== 'string' || !o.message.trim()) {
    return null;
  }
  return { status: o.status, responseCode: o.responseCode, message: o.message };
}

/** Anonymous, jobKey/vendorKey-only chat for the login-free work-order portal (see JobChatPublicController). */
@Injectable({ providedIn: 'root' })
export class JobChatService {
  private readonly http = inject(HttpClient);

  getMessages(
    jobKey: string,
    vendorKey: string,
    pageNumber = 1,
    pageSize = 50,
  ): Observable<JobChatMessagesGetResult> {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/job/chat/messages`;
    const params = new HttpParams()
      .set('jobKey', jobKey)
      .set('vendorKey', vendorKey)
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));

    return this.http.get<JobChatMessagesApiResponse>(path, { params }).pipe(
      map((res) => messagesResultFromEnvelope(res)),
      catchError((err): Observable<JobChatMessagesGetResult> => {
        const fromBody = messagesResultFromHttpError(err);
        if (fromBody && fromBody.outcome !== 'failure') {
          return of(fromBody);
        }
        return of({ outcome: 'failure' });
      }),
    );
  }

  sendMessage(jobKey: string, vendorKey: string, body: string): Observable<JobChatSendResult> {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/job/chat/send-message`;
    const qs = new URLSearchParams({ VendorKey: vendorKey.trim() });
    const url = `${path}?${qs.toString()}`;

    return this.http.post<JobChatSendApiResponse>(url, { jobKey, body }).pipe(
      map((res) => sendResultFromEnvelope(res)),
      catchError((err): Observable<JobChatSendResult> => {
        const fromBody = sendResultFromHttpError(err);
        if (fromBody && fromBody.outcome !== 'failure') {
          return of(fromBody);
        }
        return of({ outcome: 'failure' });
      }),
    );
  }

  /**
   * Advances the vendor's server-side read cursor for this job's thread, covering every message
   * currently in it (no timestamp needed — the server defaults to the thread max). Best-effort:
   * a failure here shouldn't disrupt the chat UI, so callers just get a boolean.
   */
  markRead(jobKey: string, vendorKey: string): Observable<boolean> {
    const base = environment.apiBaseUrl.replace(/\/$/, '');
    const path = `${base}/api/job/chat/mark-read`;
    const qs = new URLSearchParams({ VendorKey: vendorKey.trim() });
    const url = `${path}?${qs.toString()}`;

    return this.http.post<{ status: boolean }>(url, { jobKey }).pipe(
      map((res) => Boolean(res?.status)),
      catchError(() => of(false)),
    );
  }
}
